import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logCptEdit } from '@/lib/audit/logCptEdit';

interface RemoveAssistantNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId?: string;
  templateName?: string;
  caseId?: string;
  onCaseRemove: () => void;
  onTemplateUpdated?: () => void;
}

export function RemoveAssistantNoteDialog({
  open,
  onOpenChange,
  templateId,
  templateName,
  caseId,
  onCaseRemove,
  onTemplateUpdated,
}: RemoveAssistantNoteDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const handleCaseOnly = () => {
    onCaseRemove();

    if (caseId) {
      logCptEdit({
        caseId,
        action: 'remove_procedure',
        cptCode: '09980',
        section: 'secondary',
        scope: 'case_only',
        templateId,
        details: { description: 'Surgical Assistant note block removed (case only)' } as any,
      });
    }

    toast.success('Assistant note removed for this case');
    onOpenChange(false);
  };

  const handleMasterRemove = async () => {
    if (!templateId) {
      toast.error('No template linked to this case');
      return;
    }

    setIsSaving(true);
    try {
      const { data: template, error: fetchErr } = await supabase
        .from('surgeon_templates')
        .select('*')
        .eq('id', templateId)
        .single();

      if (fetchErr || !template) throw new Error('Failed to load template');

      const currentVersion = template.version ?? 1;
      const newVersion = currentVersion + 1;

      // Create version snapshot
      await supabase.from('surgeon_template_versions').insert({
        template_id: templateId,
        version: newVersion,
        primary_cpts: template.primary_cpts,
        secondary_cpts: template.secondary_cpts,
        narrative_template: template.narrative_template,
        common_addons: template.common_addons,
        assistant_note_enabled_default: false,
        change_description: 'Removed surgical assistant note block from master template',
        changed_by: 'surgeon',
      });

      // Update master template
      const { error: updateErr } = await supabase
        .from('surgeon_templates')
        .update({
          assistant_note_enabled_default: false,
          version: newVersion,
          updated_at: new Date().toISOString(),
        })
        .eq('id', templateId);

      if (updateErr) throw updateErr;

      // Remove from case too
      onCaseRemove();
      onTemplateUpdated?.();

      // Audit log
      if (caseId) {
        logCptEdit({
          caseId,
          action: 'remove_procedure',
          cptCode: '09980',
          section: 'secondary',
          scope: 'master_template',
          templateId,
          templateVersionBefore: currentVersion,
          templateVersionAfter: newVersion,
          details: { description: 'Surgical assistant note block removed from master template' } as any,
        });
      }

      toast.success(`Template updated to v${newVersion} — assistant note removed`);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update template');
    } finally {
      setIsSaving(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove Surgical Assistant?</DialogTitle>
          <DialogDescription>
            Removing CPT 09980 will delete the surgical assistant documentation for this case.
            This will not permanently change the master template unless selected below.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/20">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            Choose whether this change applies only to this case or permanently updates the master template.
          </p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button variant="outline" onClick={handleCaseOnly} className="w-full">
            Remove for This Case Only
          </Button>
          <Button
            variant="destructive"
            onClick={handleMasterRemove}
            disabled={isSaving || !templateId}
            className="w-full"
          >
            {isSaving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Remove and Update Master Template
          </Button>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
